import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// Yearly revenue budget vs actual, per SKU — a brand's annual plan at a glance.
// Budget = full-year target (cases) × case price; actual = sold-to-date × price.
// Deliberately NEUTRAL on pace: on/behind-pace depends on the operating-year
// start (a June launch reads "behind" against a calendar year), so we show the
// numbers and let the operator read them — no misleading verdict.

const d = (v: number) => '$' + Math.round(v || 0).toLocaleString('en-US');
const dk = (v: number) => '$' + Math.round((v || 0) / 1000) + 'k';

export default function YearlyBudget({
  skus = [], priceByCode = {}, year,
}: { skus?: any[]; priceByCode?: Record<string, number>; year?: number }) {
  const rows = useMemo(() =>
    skus.map((k: any) => {
      const price = (priceByCode[k.code] || 0) / 100; // $ per case
      const budget = (k.fy || 0) * price;
      const actual = (k.ytd || 0) * price;
      return {
        name: String(k.name || k.code).replace(/ 12-pack| granola/ig, '').trim(),
        budget: Math.round(budget),
        actual: Math.round(actual),
        pct: budget ? Math.round((actual / budget) * 100) : 0,
      };
    }).filter((r) => r.budget > 0).sort((a, b) => b.budget - a.budget),
  [skus, priceByCode]);

  if (!rows.length) return null;

  const totBudget = rows.reduce((s, r) => s + r.budget, 0);
  const totActual = rows.reduce((s, r) => s + r.actual, 0);
  const pct = totBudget ? Math.round((totActual / totBudget) * 100) : 0;
  const remaining = Math.max(0, totBudget - totActual);

  return (
    <div className="space-y-3">
      <h2 className="font-semibold text-slate-900">Yearly budget{year ? ` — ${year}` : ''}</h2>
      <div className="grid grid-cols-3 gap-3">
        <div className="card p-4">
          <p className="text-xs uppercase text-slate-400 mb-1">Annual revenue budget</p>
          <p className="text-2xl font-bold text-slate-900">{d(totBudget)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase text-slate-400 mb-1">Actual to date</p>
          <p className="text-2xl font-bold text-slate-900">{d(totActual)}</p>
          <p className="text-xs text-slate-400">{pct}% of budget</p>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase text-slate-400 mb-1">Remaining to plan</p>
          <p className="text-2xl font-bold text-slate-900">{d(remaining)}</p>
        </div>
      </div>
      <div className="card p-4">
        <h3 className="font-semibold text-slate-900 text-sm mb-2">Budget vs actual by product</h3>
        <div style={{ height: 224 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ left: 4, right: 8, top: 8, bottom: 4 }} barGap={2}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={44} tickFormatter={dk} />
              <Tooltip formatter={(v: any, n: any) => [d(Number(v)), n === 'budget' ? 'Budget' : 'Actual']} cursor={{ fill: '#f1f5f9' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="budget" name="Budget" fill="#cbd5e1" radius={[4, 4, 0, 0]} barSize={22} />
              <Bar dataKey="actual" name="Actual" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
